import { motion, AnimatePresence } from 'framer-motion'
import { useLiveStream } from '../hooks/useLiveStream'
import { AGENT_CONFIG } from '../types'

// Format relative time
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Get category badge color
function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    insight: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    decision: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    pattern: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
    action: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    learning: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
  }
  return colors[category.toLowerCase()] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
}

// Get agent avatar
function AgentAvatar({ agentId }: { agentId: string }) {
  const config = AGENT_CONFIG[agentId.toLowerCase()] || {
    name: agentId,
    color: '#888888',
    ringClass: 'default-ring'
  }

  return (
    <div 
      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
      style={{ 
        backgroundColor: `${config.color}20`,
        border: `2px solid ${config.color}40`,
        color: config.color
      }}
    >
      {config.name.charAt(0)}
    </div>
  )
}

export default function LiveStream() {
  const { observations, loading, error } = useLiveStream({ limit: 20, pollInterval: 10000 })

  if (loading) {
    return (
      <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 h-full">
        <h3 className="text-lg font-semibold text-white mb-4">Live Stream</h3>
        <div className="flex items-center justify-center h-64">
          <motion.div
            className="w-8 h-8 border-4 border-gray-700 border-t-cyan-500 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 h-full">
        <h3 className="text-lg font-semibold text-white mb-4">Live Stream</h3>
        <div className="flex items-center justify-center h-64 text-red-400">
          <span className="text-sm">Failed to load stream</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Live Stream</h3>
        <div className="flex items-center gap-2">
          <motion.div
            className="w-2 h-2 rounded-full bg-emerald-500"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <span className="text-xs text-gray-500">Live</span>
        </div>
      </div>

      {/* Stream list */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-2 max-h-[500px]">
        <AnimatePresence mode="popLayout">
          {observations.map((obs, index) => (
            <motion.div
              key={obs.id}
              layout
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ delay: index * 0.05 }}
              className="flex gap-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors cursor-pointer group"
            >
              <AgentAvatar agentId={obs.agent_id} />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-300">
                    {AGENT_CONFIG[obs.agent_id.toLowerCase()]?.name || obs.agent_id}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${getCategoryColor(obs.category)}`}>
                    {obs.category}
                  </span>
                </div>
                
                <p className="text-sm text-gray-400 line-clamp-2 group-hover:text-gray-300 transition-colors">
                  {obs.content}
                </p>
                
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-gray-500">
                    {formatRelativeTime(obs.timestamp)}
                  </span>
                  {obs.confidence > 0.8 && (
                    <span className="text-xs text-emerald-500">
                      High confidence
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {observations.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <div className="text-4xl mb-2">📡</div>
            <span className="text-sm">No observations yet</span>
          </div>
        )}
      </div>

      {/* Bottom fade */}
      <div className="h-8 bg-gradient-to-t from-gray-900/50 to-transparent pointer-events-none" />
    </div>
  )
}
